// The InFront box's evidence (pipeline RC14 on track A's RC10): what says how the client takes InFront's streams, each
// passage quoted with the day it was said, who said it and a way to open where it was said; then what Cobrain read as
// saying so, for a person to confirm or dismiss, each undone in place. Each reader is given only what they may read
// (RC10), so nothing here filters. The list shows only where the read carries RC10's field.
import { ActionButton } from '../parts/index.js';
import { useState } from 'react';
import { records } from '../api';
import { opensPath } from '../deals/live-words';
import { plateDay, type CompanyRead, type InFrontEvidence as Evidence } from './company-read';

type Ev = NonNullable<CompanyRead['inFrontEvidence']>;

/** The proposed passages shown before "Show the other N"; a passage longer than LONG shows four lines until opened. */
const SHOWN = 3;
const LONG = 280;

/** Where a piece of evidence opens: its call, or the page that holds its message; null where no page does (D184). */
export function evidencePath(e: Evidence, store: string): string | null {
  if (e.meetingId) return `/meetings/${encodeURIComponent(e.meetingId)}`;
  return e.signalId && e.opens ? opensPath(e.signalId, e.opens, store) : null;
}

/** What a passage says of the route (RC10's five readings), in words; nothing where Cobrain could not tell. */
export function routeWords(route: string | null): string | null {
  switch (route) {
    case 'direct': return 'Says it went direct to InFront';
    case 'other_supplier': return 'Says it takes them from another supplier';
    case 'stopped': return 'Says it stopped taking them';
    case 'through_igame': return 'Says it still takes them through iGame';
    default: return null;
  }
}

/** "4 Feb 2026, Mike Thomas, in a mail": when, who and where, in the order a person asks them. */
export function evidenceWhen(e: Evidence): string {
  // RC10's kinds as live holds them, 7 Oct: meeting, email, teams_chat, teams_channel.
  const where = e.meetingId || e.kind === 'meeting' ? 'on a call' : e.kind === 'teams_channel' ? 'in a Teams channel'
    : e.kind === 'teams_chat' || e.kind === 'chat' ? 'in a Teams chat' : e.kind === 'mail' || e.kind === 'email' ? 'in a mail' : null;
  return [plateDay(e.occurredAt.slice(0, 10)), e.saidBy, where].filter(Boolean).join(', ');
}

export function InFrontEvidenceList({ storeId, companyId, name, read, mayEdit, open, onChange }: {
  storeId: string; companyId: string; name: string; read: CompanyRead; mayEdit: boolean; open: (path: string) => void; onChange: (ev: Ev) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Evidence | null>(null);
  // Live, 7 Oct: Bet365 had 13 proposed passages of up to 900 characters, and the box ran to 2,966 px at 1440. Each
  // passage shows four lines until opened, and the proposed list its strongest three until asked for the rest.
  const [whole, setWhole] = useState<ReadonlySet<string>>(new Set());
  const [allProposed, setAllProposed] = useState(false);
  const ev = read.inFrontEvidence;
  if (!ev || (ev.confirmed.length === 0 && ev.proposed.length === 0 && !dismissed)) return null;
  const decide = async (e: Evidence, decision: 'confirm' | 'dismiss' | 'undo') => {
    setBusy(e.id); setWrong(null);
    try {
      const a = await records.decideEvidence(storeId, companyId, e.id, decision);
      onChange(a.inFrontEvidence);
      setDismissed(decision === 'dismiss' ? e : null);
    } catch (x) { setWrong(`Not saved: ${x instanceof Error ? x.message.replace(/\.$/, '') : String(x)}.`); } finally { setBusy(null); }
  };
  const row = (e: Evidence, controls: React.ReactNode) => {
    const path = evidencePath(e, storeId);
    return (
      <li key={e.id} className="if-ev">
        <span className="if-when">{evidenceWhen(e)}</span>
        {routeWords(e.route) ? <b className="if-route">{routeWords(e.route)}</b> : null}
        <q className={whole.has(e.id) || e.passage.length <= LONG ? 'conv-said' : 'conv-said if-clamp'}>{e.passage}</q>
        <span className="if-ev-act">
          {e.passage.length > LONG && !whole.has(e.id)
            ? <ActionButton reading type="button" className="hx-link" onClick={() => setWhole((w) => new Set([...w, e.id]))}>Read it all</ActionButton> : null}
          {path ? <a className="hx-link" href={path} onClick={(x) => { x.preventDefault(); open(path); }}>{e.subject ? `Open "${e.subject}"` : 'Open where it was said'}</a> : null}
          {controls}
        </span>
      </li>
    );
  };
  return (
    <div className="if-evidence" id="a-infront-evidence">
      {ev.confirmed.length ? (
        <>
          <p className="colh">What says how {name} takes InFront's streams</p>
          <ul className="if-evs">
            {ev.confirmed.map((e) => row(e, mayEdit
              ? <ActionButton type="button" className="hx-link" disabled={busy !== null} onClick={() => void decide(e, 'undo')}>{busy === e.id ? 'Undoing' : 'Undo'}</ActionButton>
              : null))}
          </ul>
        </>
      ) : null}
      {ev.proposed.length ? (
        <>
          <p className="colh">What Cobrain read as saying how {name} takes them; each counts once a person confirms it</p>
          <ul className="if-evs">
            {(allProposed ? ev.proposed : ev.proposed.slice(0, SHOWN)).map((e) => row(e, mayEdit ? (
              <span className="opts">
                <ActionButton type="button" className="opt" disabled={busy !== null} onClick={() => void decide(e, 'confirm')}>{busy === e.id ? 'Saving' : 'Confirm'}</ActionButton>
                <ActionButton type="button" className="opt" disabled={busy !== null} onClick={() => void decide(e, 'dismiss')}>Not about this</ActionButton>
              </span>
            ) : null))}
          </ul>
          {!allProposed && ev.proposed.length > SHOWN
            ? <ActionButton reading type="button" className="hx-link if-more" onClick={() => setAllProposed(true)}>Show the other {ev.proposed.length - SHOWN}</ActionButton> : null}
        </>
      ) : null}
      {dismissed ? (
        <p className="if-said" role="status">Dismissed the passage of {plateDay(dismissed.occurredAt.slice(0, 10))}.{' '}
          {mayEdit ? <ActionButton type="button" className="hx-link" disabled={busy !== null} onClick={() => void decide(dismissed, 'undo')}>Undo</ActionButton> : null}
        </p>
      ) : null}
      {wrong ? <span className="awrong" role="alert">{wrong}</span> : null}
    </div>
  );
}
